// src/utils/api_dashboard.js
//
// Llamadas HTTP para el nuevo dashboard de EGPro (evento, peleadores,
// inscripciones, boletos).
// Base: dashboard/
// Usa admin_token igual que api_admin.js

import axios from 'axios'
import router from '@/router'
import { useAdminStore } from '@/store/adminStore'
import Swal from 'sweetalert2'

const API_KEY    = process.env.VUE_APP_API_KEY
const apiBaseUrl = process.env.VUE_APP_API

export const apiDashboard = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    'Accept':        'application/json',
  },
})

apiDashboard.interceptors.request.use(config => {
  if (API_KEY) config.headers['x-api-key'] = API_KEY
  const token = localStorage.getItem('admin_token')
  if (token) config.headers['Authorization'] = `Bearer ${token}`

  // Si el body es FormData, eliminar el Content-Type para que el browser
  // lo genere automáticamente con el boundary correcto.
  // El default 'application/json' definido en axios.create() lo sobreescribiría
  // de lo contrario, y DRF no podría parsear el archivo.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
}, error => Promise.reject(error))

// ✅ FIX: token vencido/inválido — el guard del router (`requiresDashboardAuth`)
// solo revisa que admin_token EXISTA, no que siga siendo válido. Si expira a
// mitad de sesión, antes te quedabas atorado dentro del dashboard con cada
// request tronando en 401 sin que nada te sacara al login.
//
// Este interceptor atrapa cualquier 401 que venga de esta instancia, limpia
// la sesión (store.logout(), que ya se encarga de admin_token + estado de
// Pinia) y redirige a /dashboard/login. El flag evita mostrar el mismo
// aviso varias veces si hay varios requests en paralelo fallando a la vez.
let sesionExpiradaEnCurso = false

apiDashboard.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response?.status

    if (status === 401 && !sesionExpiradaEnCurso) {
      sesionExpiradaEnCurso = true

      const store = useAdminStore()
      store.logout()

      await Swal.fire({
        icon: 'info',
        title: 'Sesión expirada',
        text: 'Tu sesión ha vencido. Inicia sesión de nuevo para continuar.',
        confirmButtonColor: '#f3bc2c',
        confirmButtonText: 'Entendido',
      })

      sesionExpiradaEnCurso = false

      if (router.currentRoute.value.name !== 'DashboardLogin') {
        router.push({ name: 'DashboardLogin' })
      }
    }

    return Promise.reject(error)
  }
)


// ─────────────────────────────────────────────────────────────────────────────
// RESUMEN
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Snapshot del evento activo: datos + conteos de peleadores + boletos.
 */
export const getResumenDashboard = async () => {
  const res = await apiDashboard.get('dashboard/resumen/')
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// EVENTO
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Devuelve los datos del evento activo (mismo endpoint público, mismo token admin).
 */
export const getEventoActivoDashboard = async () => {
  const res = await apiDashboard.get('evento/activo/')
  return res.data
}

/**
 * Edita el evento activo (PATCH parcial).
 * @param {Number} id
 * @param {Object} payload
 */
export const editarEventoDashboard = async (id, payload) => {
  // Ruta a definir en el back cuando se implemente la vista de edición de evento
  const res = await apiDashboard.patch(`dashboard/evento/${id}/`, payload)
  return res.data
}


// ─────────────────────────────────────────────────────────────────────────────
// PELEADORES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lista todos los peleadores del evento activo.
 * @param {Object} filtros  - { estatus, rol }  (opcionales)
 */
export const getPeleadoresDashboard = async (filtros = {}) => {
  const res = await apiDashboard.get('dashboard/peleadores/', { params: filtros })
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

/**
 * Ver / editar un peleador individual.
 * @param {Number} id
 */
export const getPeleadorDetalle = async (id) => {
  const res = await apiDashboard.get(`dashboard/peleadores/${id}/`)
  return res.data
}

/**
 * Editar parcialmente un peleador (PATCH).
 * @param {Number}           id
 * @param {Object|FormData}  payload  — Object para campos de texto, FormData si incluye foto.
 *
 * Cuando se pasa un FormData, axios lo detecta automáticamente y agrega el
 * Content-Type: multipart/form-data con el boundary correcto.
 * NO se fuerza el header manualmente porque eso rompería el boundary.
 */
export const editarPeleador = async (id, payload) => {
  const res = await apiDashboard.patch(`dashboard/peleadores/${id}/`, payload)
  return res.data
}

/**
 * Alta masiva de peleadores.
 * @param {Array} peleadores  — array de objetos con campos del peleador + notificar
 * Devuelve { resultados: [ { indice, ok, email, peleador_id, errores } ] }
 */
export const altaMasivaPeleadores = async (peleadores) => {
  const res = await apiDashboard.post('dashboard/peleadores/alta-masiva/', peleadores)
  return res.data
}

// ── Gafetes (pase de identificación) ────────────────────────────────────────

/**
 * Gafete individual de un peleador (PDF listo para imprimir).
 * @param {Number} id
 */
export const getGafetePeleadorPdf = async (id) => {
  const res = await apiDashboard.get(`dashboard/peleadores/${id}/gafete/`, { responseType: 'blob' })
  return res.data
}

/**
 * PDF con los gafetes de todos los peleadores confirmados del evento activo.
 */
export const getGafetesPeleadoresPdf = async () => {
  const res = await apiDashboard.get('dashboard/peleadores/gafetes/pdf/', { responseType: 'blob' })
  return res.data
}



// ─────────────────────────────────────────────────────────────────────────────
// INSCRIPCIONES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Obtiene todas las inscripciones del evento activo.
 * @param {Object} params  { estatus?: string, q?: string }
 */
export async function getInscripcionesDashboard(params = {}) {
  const res = await apiDashboard.get('dashboard/inscripciones/', { params })
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

/**
 * Cambia el estatus de una inscripción (confirmado / rechazado).
 * @param {number} inscripcionId
 * @param {{ estatus: string, motivo_rechazo?: string }} payload
 */
export async function cambiarEstatusInscripcion(inscripcionId, payload) {
  const res = await apiDashboard.patch(`dashboard/inscripciones/${inscripcionId}/`, payload)
  return res.data
}



// ─────────────────────────────────────────────────────────────────────────────
// BOLETOS
// ─────────────────────────────────────────────────────────────────────────────

// ── Resumen ───────────────────────────────────────────────────────────────────
 
/**
 * Cards financieras del evento activo.
 * Devuelve: total_recaudado, boletos_vendidos, boletos_cupo_total,
 *           boletos_disponibles, compras_pagadas, compras_pendientes,
 *           compras_reembolsadas
 */
export const getResumenBoletosDashboard = async () => {
  const res = await apiDashboard.get('dashboard/boletos/resumen/')
  return res.data
}
 
// ── Tipos ─────────────────────────────────────────────────────────────────────
 
/**
 * Lista todos los TipoBoleto del evento activo.
 * @param {Object} params  { activo?: 'true'|'false', tipo?: 'general'|'primera_fila'|'mesa_vip'|'streaming' }
 */
export const getTiposBoletosDashboard = async (params = {}) => {
  const res = await apiDashboard.get('dashboard/boletos/tipos/', { params })
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}
 
/**
 * Crea un nuevo TipoBoleto.
 * @param {Object} payload  { nombre, tipo, precio, cupo, orden, activo, destacado, ... }
 */
export const crearTipoBoletoDashboard = async (payload) => {
  const res = await apiDashboard.post('dashboard/boletos/tipos/', payload)
  return res.data
}
 
/**
 * Edita parcialmente un TipoBoleto (PATCH).
 * También sirve para el toggle de activo: { activo: false }
 * @param {Number} id
 * @param {Object} payload
 */
export const editarTipoBoletoDashboard = async (id, payload) => {
  const res = await apiDashboard.patch(`dashboard/boletos/tipos/${id}/`, payload)
  return res.data
}
 
/**
 * Elimina un TipoBoleto.
 * El backend devuelve 409 si ya tiene compras registradas.
 * @param {Number} id
 */
export const eliminarTipoBoletoDashboard = async (id) => {
  await apiDashboard.delete(`dashboard/boletos/tipos/${id}/`)
}
 
// ── Compras (solo lectura) ────────────────────────────────────────────────────
 
/**
 * Lista todas las compras del evento activo con datos de comprador,
 * tipo de boleto y asignaciones embebidas.
 * @param {Object} params  { status_pago?, tipo?, q? }
 */
export const getComprasDashboard = async (params = {}) => {
  const res = await apiDashboard.get('dashboard/boletos/compras/', { params })
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

// ── Boletos físicos (venta en taquilla) ────────────────────────────────────────

/**
 * Historial de lotes de boletos físicos generados (para reimprimir).
 */
export const getLotesFisicosDashboard = async () => {
  const res = await apiDashboard.get('dashboard/boletos/fisicos/')
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

/**
 * Genera un lote de boletos físicos para un TipoBoleto y regresa el PDF
 * ya compuesto (listo para imprimir) como blob.
 * @param {Number} tipoId
 * @param {Number} cantidad
 */
export const generarBoletosFisicos = async (tipoId, cantidad) => {
  const res = await apiDashboard.post(
    `dashboard/boletos/tipos/${tipoId}/generar-fisicos/`,
    { cantidad },
    { responseType: 'blob' }
  )
  return res.data
}

/**
 * Vuelve a descargar el PDF de un lote ya generado (sin regenerar QR).
 * @param {Number} compraId
 */
export const redescargarPdfLote = async (compraId) => {
  const res = await apiDashboard.get(
    `dashboard/boletos/fisicos/${compraId}/pdf/`,
    { responseType: 'blob' }
  )
  return res.data
}

// ─────────────────────────────────────────────────────────────────────────────
// BENEFICIOS
// ─────────────────────────────────────────────────────────────────────────────
 
/**
 * Lista todos los beneficios disponibles (catálogo global).
 */
export const getCatalogoBeneficios = async () => {
  const res = await apiDashboard.get('dashboard/boletos/beneficios/')
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}
 
/**
 * Crea un nuevo beneficio en el catálogo.
 * @param {{ nombre: string }} payload
 */
export const crearBeneficio = async (payload) => {
  const res = await apiDashboard.post('dashboard/boletos/beneficios/', payload)
  return res.data
}

// ─────────────────────────────────────────────────────────────────────────────
// PELEADORES MÁS ELEGIDOS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ranking de peleadores más elegidos por los compradores, agrupado por
 * tipo de boleto. Cada boleto de una compra cuenta entero para el
 * peleador que el comprador eligió al comprar (no se prorratea entre
 * asistentes).
 * Devuelve: [{ tipo_boleto_id, tipo_boleto_nombre, total_boletos_con_peleador,
 *              peleadores: [{ peleador_id, nombre, apellido, foto, boletos }] }]
 */
export const getPeleadoresPopularesDashboard = async () => {
  const res = await apiDashboard.get('dashboard/boletos/peleadores-populares/')
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

/**
 * Ranking global (top 15) de peleadores por total de boletos vendidos con
 * ellos elegidos, sumando TODOS los tipos de boleto — a diferencia de
 * getPeleadoresPopularesDashboard, que agrupa por tipo. El front decide
 * cuántos mostrar (5/10/15) sin volver a pedir al backend.
 */
export const getPeleadoresTopBoletosDashboard = async () => {
  const res = await apiDashboard.get('dashboard/boletos/peleadores-top/')
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

// ─────────────────────────────────────────────────────────────────────────────
// ASISTENTES DE PELEADOR (ESQUINAS)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lista los asistentes/esquinas registrados.
 * @param {Object} filtros  - { peleador_id?, activo?, q? }
 */
export const getAsistentesDashboard = async (filtros = {}) => {
  const res = await apiDashboard.get('dashboard/asistentes-peleador/', { params: filtros })
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}

/**
 * Crea un asistente para un peleador.
 * @param {{ nombre, apellido, correo, peleador, activo }} payload
 */
export const crearAsistenteDashboard = async (payload) => {
  const res = await apiDashboard.post('dashboard/asistentes-peleador/', payload)
  return res.data
}

/**
 * Edita parcialmente un asistente (PATCH). También sirve para el toggle de activo.
 * @param {Number} id
 * @param {Object} payload
 */
export const editarAsistenteDashboard = async (id, payload) => {
  const res = await apiDashboard.patch(`dashboard/asistentes-peleador/${id}/`, payload)
  return res.data
}

/**
 * Elimina un asistente.
 * @param {Number} id
 */
export const eliminarAsistenteDashboard = async (id) => {
  await apiDashboard.delete(`dashboard/asistentes-peleador/${id}/`)
}

// ── Gafetes (pase de identificación) ────────────────────────────────────────

/**
 * Gafete individual de un asistente de peleador (PDF listo para imprimir).
 * @param {Number} id
 */
export const getGafeteAsistentePdf = async (id) => {
  const res = await apiDashboard.get(`dashboard/asistentes-peleador/${id}/gafete/`, { responseType: 'blob' })
  return res.data
}

/**
 * PDF con los gafetes de todos los asistentes activos con peleador confirmado.
 */
export const getGafetesAsistentesPdf = async () => {
  const res = await apiDashboard.get('dashboard/asistentes-peleador/gafetes/pdf/', { responseType: 'blob' })
  return res.data
}

// ─────────────────────────────────────────────────────────────────────────────
// PATROCINADORES
// ─────────────────────────────────────────────────────────────────────────────
 
/**
 * Lista los patrocinadores del evento activo.
 * @param {Object} filtros  - { confirmado?, activo? }  ('true'|'false', opcionales)
 */
export const getPatrocinadoresDashboard = async (filtros = {}) => {
  const res = await apiDashboard.get('dashboard/patrocinadores/', { params: filtros })
  return Array.isArray(res.data) ? res.data : res.data?.results ?? []
}
 
/**
 * Crea un nuevo patrocinador para el evento activo.
 * @param {Object|FormData} payload  — FormData si incluye logo nuevo.
 */
export const crearPatrocinadorDashboard = async (payload) => {
  const res = await apiDashboard.post('dashboard/patrocinadores/', payload)
  return res.data
}
 
/**
 * Edita parcialmente un patrocinador (PATCH).
 * También sirve para toggles rápidos: { confirmado: true } o { activo: false }.
 * @param {Number}          id
 * @param {Object|FormData} payload
 */
export const editarPatrocinadorDashboard = async (id, payload) => {
  const res = await apiDashboard.patch(`dashboard/patrocinadores/${id}/`, payload)
  return res.data
}
 
/**
 * Elimina un patrocinador.
 * @param {Number} id
 */
export const eliminarPatrocinadorDashboard = async (id) => {
  await apiDashboard.delete(`dashboard/patrocinadores/${id}/`)
}